/**
 * A worked example — EST-2482, the front-left F-150 every mockup shows.
 *
 * FIXTURE. This exists so the screens can be used and demonstrated before a
 * database exists, and so the numbers on screen match the golden test in
 * `src/calc/engine.test.ts`. It is not seed data and must not be shipped: the
 * screens take their data as props precisely so this can be swapped for a query
 * without touching them.
 */

import type { Candidate } from '../ai/review.js';
import type { ImpactPoint, ShotState } from '../capture/shotlist.js';

import { CATALOG_ROWS } from './shop.js';
import type { CannotAssess } from '../ai/review.js';

export const FIXTURE_IMPACT: ImpactPoint = 'front_left';

/**
 * Deliberately incomplete: the engine bay is outstanding and the LH fender was
 * shot flat-on. Those two gaps are what make the gate visible on screen.
 */
export const FIXTURE_SHOTS: ShotState[] = [
  { id: 'VIN', status: 'taken' },
  { id: 'PAINT', status: 'taken' },
  { id: 'WIDE_L', status: 'taken' },
  { id: 'F_BUMPER', status: 'taken' },
  { id: 'HOOD', status: 'taken' },
  { id: 'Q_LH_FRONT', status: 'taken' },
  { id: 'LAMP_LH', status: 'taken' },
  { id: 'PANEL_LH', status: 'retake', reason: 'shot flat-on, needs a raking angle' },
  { id: 'ENGINE_BAY', status: 'todo' },
];

export const FIXTURE_CANDIDATES: Candidate[] = [
  {
    id: 'fx-front-bumper', zone: 'front_bumper', partLabel: 'Bumper cover, front',
    damageKind: 'deformation', operation: 'replace', workKind: 'body', confidence: 92,
    evidence: ['Q_LH_FRONT', 'F_BUMPER', 'LAMP_LH'],
    observations: [
      'Deformation across the full left half, past the fog-lamp aperture',
      'Mounting tab at the LH end fractured — visible in LAMP_LH',
    ],
    rationale: 'Seen in three of three relevant photographs, consistent across angles.',
  },
  {
    id: 'fx-left-headlamp', zone: 'left_headlamp', partLabel: 'Headlamp assembly, LH',
    damageKind: 'broken mount', operation: 'replace', workKind: 'body', confidence: 88,
    evidence: ['Q_LH_FRONT', 'LAMP_LH'],
    observations: [
      'Lens intact but the lower mounting tab is broken',
      'Housing displaced rearward against the fender line',
    ],
    rationale: 'Not visible in F_BUMPER, which is the wrong angle, so 88 rather than 95.',
  },
  {
    id: 'fx-left-fender', zone: 'left_fender', partLabel: 'Fender, LH',
    damageKind: 'crease', operation: 'repair', workKind: 'body', confidence: 74,
    evidence: ['Q_LH_FRONT'],
    observations: [
      'Crease running rearward from the headlamp aperture',
      'PANEL_LH is shot flat-on, so depth cannot be judged',
    ],
    rationale: 'Damage certain, extent uncertain. Capped by the poor angle.',
  },
  {
    id: 'fx-hood', zone: 'hood', partLabel: 'Hood — leading edge',
    damageKind: 'misalignment', operation: 'repair', workKind: 'body', confidence: 61,
    evidence: ['Q_LH_FRONT', 'HOOD'],
    observations: [
      'Possible misalignment at the LH leading corner',
      'Could equally be gap tolerance rather than damage',
    ],
    rationale: 'Inferred from panel-gap asymmetry only. Low confidence is the honest answer.',
  },
  {
    id: 'fx-fog-lamp', zone: 'front_bumper', partLabel: 'Fog lamp, LH',
    damageKind: 'unknown', operation: 'rni', workKind: 'body', confidence: 55,
    evidence: ['F_BUMPER'],
    observations: ['Lens obscured by bumper deformation — the unit itself cannot be confirmed'],
    rationale: 'Single photograph, view obstructed. Proposed because it sits inside the damaged area.',
  },
  {
    // The case the gate exists for: likely involved, never photographed.
    id: 'fx-radiator-support', zone: 'radiator_support', partLabel: 'Radiator support — possible intrusion',
    operation: 'inspect', workKind: 'mechanical', confidence: 47,
    evidence: [],
    observations: [
      'No engine-bay photograph exists',
      'A frontal impact of this severity commonly reaches the support',
    ],
    rationale: 'Inferred from impact direction alone.',
  },
];

// ── the rest of the worked example ──────────────────────────────────────────

import type { CatalogEntry, ExclusionRule } from '../estimate/build.js';
import type { RateProfile } from '../calc/types.js';

/**
 * What the model could not judge.
 *
 * Taken from the first real vehicle tested (2019 Honda HR-V, rear impact,
 * 2026-08-24). The shop replaced the bumper cover AND the reinforcement bar;
 * the model proposed the cover and put the reinforcement here, because it is
 * behind the cover and no photograph from outside can show it.
 */
export const FIXTURE_CANNOT_ASSESS: CannotAssess[] = [
  { partLabel: 'Rear bumper reinforcement bar',
    reason: 'Behind the cover — not visible in any photograph taken from outside',
    photographWanted: 'Cover removed, or a raking shot along the corner showing how far it is set back' },
  { partLabel: 'Rear bumper absorber',
    reason: 'Behind the cover',
    photographWanted: 'Same shot as the reinforcement bar' },
  { partLabel: 'Left quarter panel',
    reason: 'Only the rearmost edge is in frame',
    photographWanted: 'Three-quarter rear left, two paces back' },
];

export const FIXTURE_CUSTOMER = {
  name: 'Northgate Plumbing',
  needsApproval: true,
  terms: 'NET 30',
  email: 'accounts@northgateplumbing.ca' as string | null,
};

/** Riverbend's door rates. Same numbers as the golden test. */
export const FIXTURE_PROFILE: RateProfile = {
  labourRates: { body: 85, refinish: 85, frame: 100, mechanical: 125 },
  paintMaterialsPerHour: 42,
  shopMaterialsPerHour: 22,
  taxes: [{ name: 'GST', rate: 5, onParts: true, onLabour: true, onMaterials: true }],
  taxablePartTypes: ['new_oem', 'aftermarket', 'used'],
};

/**
 * A partial catalog on purpose: the fog lamp is missing, so the "not in your
 * catalog" prompt fires the way it would in a real shop's first week.
 */
/** The job prices against the shop's catalog — there is only one. */
export const FIXTURE_CATALOG: CatalogEntry[] = CATALOG_ROWS;

export const FIXTURE_EXCLUSIONS: ExclusionRule[] = [
  {
    operation: 'replace',
    partPattern: 'bumper',
    excludes: 'refinishing, or accessories mounted in it',
    suggests: {
      operation: 'rni',
      description: 'R&I fog lamp — not in the bumper overhaul',
      labourType: 'body',
      partType: 'existing',
      partPrice: 0,
      quantity: 1,
      hours: 0.3,
    },
  },
];
